import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
import { useEffect, useRef, type HTMLAttributes } from "react";

import { AI_CONTEST_PAGE_TITLE, AI_CONTEST_WORK_TYPE_OPTIONS } from "../config";
import { useAiContestEnrollmentRuntime } from "../runtime";
import type { AiContestAttachment, AiContestWorkType } from "../types";
import "./AiContestEnrollmentPage.css";

const MEDIA_KIND_LABEL: Record<string, string> = {
  image: "图片",
  video: "视频",
  file: "文件",
};

/** 活路儿非遗创意作品线下展报名移动端页面。 */
export function AiContestEnrollmentFeaturePage() {
  const runtime = useAiContestEnrollmentRuntime();
  const error = runtime.localError || runtime.submitError;
  const errorRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (!error || runtime.errorScrollToken <= 0) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [error, runtime.errorScrollToken]);

  return (
    <div className="odform-page">
      <header className="odform-header">
        <span className="odform-tit-logo" aria-hidden>
          花
        </span>
        <h1 className="odform-tit">{AI_CONTEST_PAGE_TITLE}</h1>
        <p className="odform-subtitle">请填写报名信息并上传作品附件</p>
      </header>

      {runtime.result ? (
        <div className="odform">
          <p className="odform-ok">报名提交成功，可关闭本页面。</p>
          <div className="odform-ok-card">
            <div>报名编号：{runtime.result.id}</div>
            <div>姓名：{runtime.result.applicantName}</div>
            <div>作品：{runtime.result.workName}</div>
          </div>
          <p className="odform-hint">如需修改信息，请联系活动组织方处理。</p>
        </div>
      ) : (
        <form
          className="odform"
          onSubmit={(event) => {
            event.preventDefault();
            runtime.submit();
          }}
        >
          {error ? (
            <p
              ref={errorRef}
              className="odform-err"
              role="alert"
              aria-live="assertive"
            >
              {error}
            </p>
          ) : null}

          <section className="odform-section" aria-labelledby="section-applicant">
            <h2 id="section-applicant" className="odform-section-title">
              报名人信息
            </h2>
            <Field
              id="applicant_name"
              label="姓名"
              value={runtime.draft.applicantName}
              onChange={(value) => runtime.updateDraft({ applicantName: value })}
              placeholder="请输入姓名"
              required
            />
            <Field
              id="phone"
              label="电话"
              value={runtime.draft.phone}
              onChange={(value) => runtime.updateDraft({ phone: value })}
              placeholder="请输入手机号"
              inputMode="tel"
              required
            />
            <Field
              id="school"
              label="学校"
              value={runtime.draft.school}
              onChange={(value) => runtime.updateDraft({ school: value })}
              placeholder="请输入学校"
              required
            />
            <Field
              id="major"
              label="专业"
              value={runtime.draft.major}
              onChange={(value) => runtime.updateDraft({ major: value })}
              placeholder="请输入专业"
              required
            />
            <Field
              id="region"
              label="地区"
              value={runtime.draft.region}
              onChange={(value) => runtime.updateDraft({ region: value })}
              placeholder="请输入地区"
              required
            />
          </section>

          <section className="odform-section" aria-labelledby="section-work">
            <h2 id="section-work" className="odform-section-title">
              作品信息
            </h2>
            <Field
              id="work_name"
              label="作品名称"
              value={runtime.draft.workName}
              onChange={(value) => runtime.updateDraft({ workName: value })}
              placeholder="请输入作品名称"
              required
            />

            <div className="odform-field">
              <label htmlFor="work_type">作品类型</label>
              <select
                id="work_type"
                value={runtime.draft.workType}
                required
                onChange={(event) =>
                  runtime.updateDraft({
                    workType: event.target.value as AiContestWorkType | "",
                  })
                }
              >
                <option value="">请选择作品类型</option>
                {AI_CONTEST_WORK_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <Field
              id="work_size"
              label="作品尺寸"
              value={runtime.draft.workSize}
              onChange={(value) => runtime.updateDraft({ workSize: value })}
              placeholder="请输入作品尺寸大小"
              required
            />

            <div className="odform-field">
              <label htmlFor="remark">其他备注</label>
              <textarea
                id="remark"
                value={runtime.draft.remark}
                placeholder="选填"
                rows={3}
                onChange={(event) => runtime.updateDraft({ remark: event.target.value })}
              />
            </div>
          </section>

          <section className="odform-section" aria-labelledby="section-files">
            <div className="odform-section-head">
              <h2 id="section-files" className="odform-section-title">
                作品附件
              </h2>
              <label
                className="odform-upload-btn"
                aria-disabled={runtime.uploading || runtime.submitting}
              >
                {runtime.uploading ? "上传中..." : "选择并上传"}
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx,.zip,.rar,.7z"
                  disabled={runtime.uploading || runtime.submitting}
                  onChange={(event) => {
                    const files = event.target.files;
                    if (files) {
                      void runtime.addFiles(files);
                    }
                    event.target.value = "";
                  }}
                />
              </label>
            </div>
            <p className="odform-section-hint">
              支持图片、视频、文件，数量不限；必选上传效果图片
            </p>

            {runtime.attachments.length > 0 ? (
              <ul className="odform-file-list">
                {runtime.attachments.map((item) => (
                  <AttachmentItem
                    key={item.mediaKey}
                    item={item}
                    disabled={runtime.submitting}
                    onRemove={() => runtime.removeAttachment(item.mediaKey)}
                  />
                ))}
              </ul>
            ) : (
              <div className="odform-upload-empty">暂未上传附件，请上传效果图片</div>
            )}
          </section>

          <div className="odform-actions">
            <button
              className="odform-button"
              type="submit"
              disabled={runtime.uploading || runtime.submitting}
            >
              {runtime.submitting ? "提交中..." : "提交报名信息"}
            </button>
            <p className="odform-hint">提交成功后将保存报名与附件信息，请确认内容无误。</p>
          </div>
        </form>
      )}
    </div>
  );
}

type FieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  required?: boolean;
};

/** 报名表单输入字段。 */
function Field(props: FieldProps) {
  return (
    <div className="odform-field">
      <label htmlFor={props.id}>{props.label}</label>
      <input
        id={props.id}
        value={props.value}
        placeholder={props.placeholder}
        inputMode={props.inputMode}
        required={props.required}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </div>
  );
}

type AttachmentItemProps = {
  item: AiContestAttachment;
  disabled: boolean;
  onRemove: () => void;
};

/** 已上传附件预览与移除。 */
function AttachmentItem(props: AttachmentItemProps) {
  const { item } = props;
  return (
    <li className="odform-file-item">
      {item.mediaKind === "image" ? (
        <img className="odform-file-thumb" src={resolveHttpResourceUrl(item.fileUrl)} alt={item.fileName} />
      ) : (
        <div className="odform-file-badge" aria-hidden>
          {MEDIA_KIND_LABEL[item.mediaKind] ?? "文件"}
        </div>
      )}
      <div className="odform-file-meta">
        <strong>{item.fileName}</strong>
        <span>{MEDIA_KIND_LABEL[item.mediaKind] ?? item.mediaKind}</span>
      </div>
      <button
        type="button"
        className="odform-file-remove"
        onClick={props.onRemove}
        disabled={props.disabled}
      >
        移除
      </button>
    </li>
  );
}
